import { EQuestionType, type TNode } from '@hubertai/screening-question-types';
import { EInterviewQualified } from 'components/HubertUI/EvaluationStatus/HubertQualifiedSelect/HubertQualifiedSelect.constants';
import { EInterviewProperty } from 'components/Selects/InterviewPropertySelect/InterviewPropertySelect.constants';
import { EConditionType, newAction, newCondition } from 'types/rules.types';
import type { TQuestionRule, TQuestionRuleAction, TQuestionRuleCondition } from 'types/rules.types';

function defaultConditionShapeForType(type: EConditionType): Partial<TQuestionRuleCondition> {
  if (type === EConditionType.InterviewProperty) {
    return { property: EInterviewProperty.Qualified, operator: undefined, value: EInterviewQualified.Qualified };
  }
  return { operator: undefined, value: [] };
}

export const targetId = (value: TQuestionRuleAction['value'][number]): string =>
  typeof value === 'string' ? value : value.id;

export function replaceCondition(
  rule: TQuestionRule,
  conditionIdx: number,
  patch: Partial<TQuestionRuleCondition>,
): TQuestionRule {
  const conditions = [...rule.conditions];
  const current = conditions[conditionIdx];
  if (!current) return rule;

  conditions[conditionIdx] = { ...current, ...patch };
  return { ...rule, conditions };
}

// newCondition()'s own defaults (type: QuestionAnswer, operator: EQUALS) only make sense together,
// for that one type — a caller overriding the type must reset the rest of the condition's shape too,
// or a freshly added condition saves a comparison operator (or, for InterviewProperty, no property at
// all) before anything meaningful has actually been chosen.
export function addCondition(rule: TQuestionRule, type: EConditionType): TQuestionRule {
  const condition = { ...newCondition(), type, ...defaultConditionShapeForType(type) };
  return { ...rule, conditions: [...rule.conditions, condition] };
}

export function removeConditionAt(rule: TQuestionRule, conditionIdx: number): TQuestionRule {
  return { ...rule, conditions: rule.conditions.filter((_, idx) => idx !== conditionIdx) };
}

// A condition's question_id only makes sense for EConditionType.QuestionAnswer — cleared for every
// other type, and reset to an unset state (not yet pointing at a question) when switching to it.
// property is the same idea for InterviewProperty. The value and operator are reset too: none of the
// other condition types share a value shape or a valid operator set with whichever one was selected
// before, so carrying either over can leave a condition showing an operator its own UI would never
// let you pick.
export function setConditionType(rule: TQuestionRule, conditionIdx: number, type: EConditionType): TQuestionRule {
  const conditions = [...rule.conditions];
  const current = conditions[conditionIdx];
  if (!current) return rule;

  const next: TQuestionRuleCondition = { ...current, type, ...defaultConditionShapeForType(type) };
  if (type === EConditionType.QuestionAnswer) {
    next.question_id = undefined;
  } else {
    delete next.question_id;
  }
  if (type !== EConditionType.InterviewProperty) {
    delete next.property;
  }

  conditions[conditionIdx] = next;
  return { ...rule, conditions };
}

// BETWEEN always starts at [0, 0] regardless of whatever the previous operator's value was — the
// two shapes aren't compatible. IS_IN/IS_NOT_IN expect an array; a single-value condition switching
// to one of these keeps its existing value by wrapping it rather than discarding it. A Form
// question's apply_to_form_id is cleared on any operator change, since which form element the
// answer must come from is a property of the previous operator's own UI, not a general one.
// operator is optional so a caller can represent "cleared back to unset" (e.g. the combobox's own
// click-again-to-deselect) without writing an empty string into saved data.
export function applyOperatorChange(
  rule: TQuestionRule,
  conditionIdx: number,
  operator: string | undefined,
  questionType?: EQuestionType,
): TQuestionRule {
  const conditions = [...rule.conditions];
  const current = conditions[conditionIdx];
  if (!current) return rule;

  let value = current.value;
  if (operator === 'BETWEEN') {
    value = [0, 0];
  } else if (operator !== undefined && ['IS_IN', 'IS_NOT_IN'].includes(operator) && typeof value === 'string') {
    value = [value];
  }

  const next: TQuestionRuleCondition = { ...current, operator, value };
  if (questionType === EQuestionType.Form && next.apply_to_form_id) {
    next.apply_to_form_id = undefined;
  }

  conditions[conditionIdx] = next;
  return { ...rule, conditions };
}

// A single/multiple-select answer is one or more option ids, not an orderable value — comparison
// operators (GREATER_THAN/LESS_THAN/BETWEEN) never had a runtime effect against either, so the
// picker shouldn't offer them. Empty means unrestricted, matching OperatorCombobox's own `include`.
const EQUALITY_MEMBERSHIP_OPERATORS = ['EQUALS', 'IS', 'IS_NOT', 'IS_IN', 'IS_NOT_IN'];

export function operatorsForQuestionType(questionType?: EQuestionType): string[] {
  if (questionType === EQuestionType.SingleSelect || questionType === EQuestionType.MultipleSelect) {
    return EQUALITY_MEMBERSHIP_OPERATORS;
  }
  return [];
}

const ANSWER_CONDITION_QUESTION_TYPES = [EQuestionType.SingleSelect, EQuestionType.MultipleSelect, EQuestionType.Form];

export function supportsAnswerCondition(questionType?: EQuestionType): boolean {
  return questionType !== undefined && ANSWER_CONDITION_QUESTION_TYPES.includes(questionType);
}

export function replaceAction(
  rule: TQuestionRule,
  actionIdx: number,
  patch: Partial<TQuestionRuleAction>,
): TQuestionRule {
  const actions = [...(rule.actions ?? [])];
  const current = actions[actionIdx];
  if (!current) return rule;

  actions[actionIdx] = { ...current, ...patch };
  return { ...rule, actions };
}

export function addAction(rule: TQuestionRule, name: string): TQuestionRule {
  return { ...rule, actions: [...(rule.actions ?? []), newAction(name)] };
}

export function removeActionAt(rule: TQuestionRule, actionIdx: number): TQuestionRule {
  return { ...rule, actions: (rule.actions ?? []).filter((_, idx) => idx !== actionIdx) };
}

export function appendActionTargets(rule: TQuestionRule, actionIdx: number, nodes: TNode[]): TQuestionRule {
  const actions = [...(rule.actions ?? [])];
  const current = actions[actionIdx];
  if (!current) return rule;

  actions[actionIdx] = { ...current, value: [...current.value, ...nodes] };
  return { ...rule, actions };
}

export function removeActionTarget(rule: TQuestionRule, actionIdx: number, targetIdx: number): TQuestionRule {
  const actions = [...(rule.actions ?? [])];
  const current = actions[actionIdx];
  if (!current) return rule;

  actions[actionIdx] = { ...current, value: current.value.filter((_, idx) => idx !== targetIdx) };
  return { ...rule, actions };
}

export function moveActionTarget(rule: TQuestionRule, actionIdx: number, from: number, to: number): TQuestionRule {
  const actions = [...(rule.actions ?? [])];
  const current = actions[actionIdx];
  if (!current) return rule;

  const value = [...current.value];
  if (from < 0 || from >= value.length || to < 0 || to >= value.length) return rule;

  [value[from], value[to]] = [value[to], value[from]];
  actions[actionIdx] = { ...current, value };
  return { ...rule, actions };
}
